import { useState, useEffect } from 'react';
import { stockyApi } from '../components/misc/StockyApi';

export const useSyncControls = (authUser, isAdmin) => {
  const [pilotSyncModeEnabled, setPilotSyncModeEnabled] = useState(false);
  const [userSyncEnabled, setUserSyncEnabled] = useState(true);
  const [globalSyncEnabled, setGlobalSyncEnabled] = useState(true);
  const [bulkPriceUpdateEnabled, setBulkPriceUpdateEnabled] = useState(false);
  const [syncControlsLoading, setSyncControlsLoading] = useState(false);
  // Employees can see the sync state but only the owner account changes it.
  const [canManageSync, setCanManageSync] = useState(true);

  const fetchSyncControls = async () => {
    if (!authUser) return;
    setSyncControlsLoading(true);
    try {
      const controlsRes = await stockyApi.getSyncControlsMe(authUser);
      setPilotSyncModeEnabled(controlsRes?.data?.pilotSyncModeEnabled === true);
      setUserSyncEnabled(controlsRes?.data?.syncEnabled !== false);
      setGlobalSyncEnabled(controlsRes?.data?.syncEnabledGlobal !== false);
      setBulkPriceUpdateEnabled(controlsRes?.data?.bulkPriceUpdateEnabled === true);
      setCanManageSync(controlsRes?.data?.canManage !== false);
    } catch (error) {
      console.error('Error fetching user controls:', error);
    } finally {
      setSyncControlsLoading(false);
    }
  };

  useEffect(() => {
    fetchSyncControls();
  }, [authUser]);

  const togglePilotMode = async () => {
    if (!authUser) return;
    const nextValue = !pilotSyncModeEnabled;
    try {
      setSyncControlsLoading(true);
      await stockyApi.updateSyncControlsMe({ pilotSyncModeEnabled: nextValue }, authUser);
      setPilotSyncModeEnabled(nextValue);
      return { success: true, nextValue };
    } catch (error) {
      console.error('Error updating pilot mode:', error);
      return { success: false, error };
    } finally {
      setSyncControlsLoading(false);
    }
  };

  const toggleUserSync = async () => {
    if (!authUser) return;
    const nextValue = !userSyncEnabled;
    try {
      setSyncControlsLoading(true);
      const res = await stockyApi.updateSyncControlsMe({ syncEnabled: nextValue }, authUser);
      setUserSyncEnabled(res?.data?.syncEnabled !== false);
      return { success: true, nextValue };
    } catch (error) {
      console.error('Error updating user sync:', error);
      return { success: false, error };
    } finally {
      setSyncControlsLoading(false);
    }
  };

  const toggleGlobalSync = async () => {
    if (!authUser || !isAdmin) return;
    const nextValue = !globalSyncEnabled;
    try {
      setSyncControlsLoading(true);
      await stockyApi.updateSyncControlsGlobal({ syncEnabledGlobal: nextValue }, authUser);
      setGlobalSyncEnabled(nextValue);
      return { success: true, nextValue };
    } catch (error) {
      console.error('Error updating global sync:', error);
      return { success: false, error };
    } finally {
      setSyncControlsLoading(false);
    }
  };

  return {
    pilotSyncModeEnabled,
    userSyncEnabled,
    globalSyncEnabled,
    bulkPriceUpdateEnabled,
    syncControlsLoading,
    canManageSync,
    togglePilotMode,
    toggleUserSync,
    toggleGlobalSync,
  };
};
